import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { Observable, combineLatest } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { LanguageService } from '@fcom/ui-translate';
import { capitalize } from '@fcom/utils';
import { CabinClassSelectionFareFamily } from '@fcom/booking/modules/ticket-selection/interfaces';
import { ButtonMode, ButtonSize, ButtonTheme, ChipTheme, IconPosition, ResponsiveImage } from '@fcom/ui-components';
import { FinnairCabinClass } from '@fcom/dapi/api/models';
import { MediaQueryService } from '@fcom/common/services';
import { GtmService } from '@fcom/common/gtm';
import { ElementActions, ElementTypes } from '@fcom/common/interfaces';

@Component({
  selector: 'fin-upsell-option',
  templateUrl: './upsell-option.component.html',
  styleUrls: ['./upsell-option.component.scss'],
})
export class UpsellOptionComponent implements OnInit {
  public readonly ButtonMode = ButtonMode;
  public readonly ButtonTheme = ButtonTheme;
  public readonly ButtonSize = ButtonSize;
  public readonly IconPosition = IconPosition;
  public readonly ChipTheme = ChipTheme;
  readonly FinnairCabinClass = FinnairCabinClass;

  @Input()
  fareFamily: CabinClassSelectionFareFamily;
  @Input()
  currencyCode$: Observable<string>;

  @Input()
  isBVariant$: Observable<boolean>;
  @Input()
  isShortHaul$: Observable<boolean>;
  @Input()
  hasNewCabin$: Observable<boolean>;
  @Input()
  isLongHaulLegAndAy$: Observable<boolean>;

  @Output()
  selectNextCabinClass: EventEmitter<CabinClassSelectionFareFamily> = new EventEmitter();

  @Output()
  openCabinClassModal: EventEmitter<string> = new EventEmitter();

  imageData$: Observable<ResponsiveImage>;
  isMobile$: Observable<boolean>;
  descriptionKey$: Observable<string>;
  hideCabinClassModalLink$: Observable<boolean>;

  constructor(
    private languageService: LanguageService,
    private httpClient: HttpClient,
    private mediaQueryService: MediaQueryService,
    private gtmService: GtmService
  ) {}

  ngOnInit(): void {
    this.isMobile$ = this.mediaQueryService.isBreakpoint$('mobile');

    const imageKey$ = combineLatest([
      this.isBVariant$,
      this.isShortHaul$,
      this.hasNewCabin$,
      this.isLongHaulLegAndAy$,
    ]).pipe(
      map(([isBVariant, isShortHaul, hasNewCabin, isLongHaulLegAndAy]) => {
        const cabinClass = capitalize(this.fareFamily.cabinClass);
        if (
          isBVariant &&
          (isShortHaul || isLongHaulLegAndAy || this.fareFamily.cabinClass === FinnairCabinClass.ECOPREMIUM)
        ) {
          const cabin = !hasNewCabin && !isShortHaul ? 'OldCabin' : '';
          const haul = isShortHaul ? 'ShortHaul' : 'LongHaul';

          return `fragments.upsell${cabinClass}${haul}${cabin}`;
        }
        return `fragments.upsell${cabinClass}`;
      })
    );

    this.descriptionKey$ = combineLatest([this.isShortHaul$, this.hasNewCabin$, this.isLongHaulLegAndAy$]).pipe(
      map(([isShortHaul, hasNewCabin, isLongHaulLegAndAy]) => {
        if (isShortHaul || isLongHaulLegAndAy || this.fareFamily.cabinClass === FinnairCabinClass.ECOPREMIUM) {
          const cabin = !hasNewCabin && !isShortHaul ? 'OldCabin' : '';
          const haul = isShortHaul ? 'ShortHaul' : 'LongHaul';

          return `upsellCard.description.${this.fareFamily.cabinClass.toLowerCase()}${haul}${cabin}`;
        }
        return `upsell.cabinClassDescription.${this.fareFamily.cabinClass.toLowerCase()}`;
      })
    );

    this.imageData$ = imageKey$.pipe(
      switchMap((imageKey) =>
        this.languageService.translate(imageKey).pipe(
          map((translation) => translation?.picture?.jsonUrl),
          filter(Boolean),
          switchMap((jsonUrl: string) => this.httpClient.get<ResponsiveImage>(jsonUrl))
        )
      )
    );

    this.hideCabinClassModalLink$ = combineLatest([this.isShortHaul$, this.isLongHaulLegAndAy$]).pipe(
      map(([isShortHaul, isLongHaulLegAndAy]) => isShortHaul || !isLongHaulLegAndAy)
    );
  }

  emitOpenCabinClassModal(event: Event, cabinClass: string): void {
    this.openCabinClassModal.emit(cabinClass);
    this.gtmService.trackElement(
      'upsell-card-modal-link',
      'booking-flow_flight-selection',
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );
    event.preventDefault();
  }
}
